import { AppId, TrackEvent, TrackerUser } from './types'

const FLUSH_INTERVAL_MS = 5000
const FLUSH_THRESHOLD = 20
const MAX_BUFFER_SIZE = 500
const API_PATH = '/v1/insight-hub/batch-track'

let appId: AppId | null = null
let user: TrackerUser | null = null
let sessionId: string | null = null
let buffer: TrackEvent[] = []
let flushTimer: ReturnType<typeof setInterval> | null = null
let initialized = false
let ending = false
let lastPage = ''

function getApiUrl(): string {
  return `${process.env.REACT_APP_PORTAL_API_URL}${API_PATH}`
}

function getAuthToken(): string | null {
  return localStorage.getItem('token')
}

function getSessionId(): string {
  let id = sessionStorage.getItem('_tracker_sid')
  if (!id) {
    // Fallback for browsers without crypto.randomUUID (React 16 / older targets)
    id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
    sessionStorage.setItem('_tracker_sid', id)
  }
  return id
}

function createEvent(
  event: TrackEvent['event'],
  page: string,
  metadata?: Record<string, unknown>,
): TrackEvent | null {
  if (!appId || !sessionId) return null

  return {
    appId,
    event,
    userId: user?.id || '',
    username: user ? `${user.firstName} ${user.lastName}` : undefined,
    email: user?.email,
    organizationId: user?.organizationId,
    organizationName: user?.organizationName,
    role: user?.role,
    sessionId,
    page,
    referrer: lastPage || document.referrer,
    metadata,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
  }
}

function pushEvent(event: TrackEvent | null): void {
  if (!event) return
  buffer.push(event)
  if (buffer.length >= FLUSH_THRESHOLD) {
    flush()
  }
}

async function flush(): Promise<void> {
  if (buffer.length === 0) return

  const events = [...buffer]
  buffer = []

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  const token = getAuthToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  try {
    await fetch(getApiUrl(), {
      method: 'POST',
      headers,
      body: JSON.stringify({ events }),
      keepalive: true,
    })
  } catch {
    // Re-queue failed events, cap buffer to prevent unbounded growth
    buffer.unshift(...events)
    if (buffer.length > MAX_BUFFER_SIZE) {
      buffer = buffer.slice(-MAX_BUFFER_SIZE)
    }
  }
}

function flushWithBeacon(): void {
  if (buffer.length === 0) return

  const events = [...buffer]
  buffer = []

  const blob = new Blob([JSON.stringify({ events })], {
    type: 'application/json',
  })

  if (navigator.sendBeacon) {
    navigator.sendBeacon(getApiUrl(), blob)
  }
}

function handleVisibilityChange(): void {
  if (document.visibilityState === 'hidden') {
    flushWithBeacon()
  }
}

function handleBeforeUnload(): void {
  endSession()
}

function endSession(): void {
  if (!initialized || ending) return
  ending = true

  pushEvent(createEvent('session_end', lastPage || window.location.pathname))
  flushWithBeacon()

  if (flushTimer) {
    clearInterval(flushTimer)
    flushTimer = null
  }
}

export function initTracker(id: AppId, trackerUser: TrackerUser): void {
  // Allow re-initialization with updated user (e.g. after re-auth)
  appId = id
  user = trackerUser

  if (initialized) return

  sessionId = getSessionId()
  initialized = true

  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS)

  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('beforeunload', handleBeforeUnload)
  window.addEventListener('pagehide', handleBeforeUnload)
}

export function trackPageView(page: string): void {
  if (!initialized) return

  pushEvent(createEvent('page_view', page))
  lastPage = page
}

export function trackSessionStart(): void {
  if (!initialized) return

  pushEvent(createEvent('session_start', window.location.pathname))
}

export function trackFeatureUse(
  feature: string,
  metadata?: Record<string, unknown>,
): void {
  if (!initialized) return

  pushEvent(createEvent('feature_use', window.location.pathname, { feature, ...metadata }))
}

export function destroyTracker(): void {
  if (flushTimer) {
    clearInterval(flushTimer)
    flushTimer = null
  }

  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('beforeunload', handleBeforeUnload)
  window.removeEventListener('pagehide', handleBeforeUnload)

  buffer = []
  initialized = false
  ending = false
  appId = null
  user = null
  sessionId = null
  lastPage = ''
}
